import React, { useState } from "react"
import * as layouts from "layouts"
import * as api from "brandguard/api"
import * as predictions from "brandguard/predictions"
import * as bgapprovals from "brandguard/components/approvals"

interface props {
	item: api.TextSearchResponseItem
	threshold: number
	display: api.Prediction
	reviewed: boolean
	color(p: api.Prediction): string
	statusText(p: api.Prediction): JSX.Element
	onChange?: (item: api.TextSearchResponseItem) => void
	reject(item: api.TextSearchResponseItem): Promise<api.TextSearchResponseItem>
	approve(item: api.TextSearchResponseItem): Promise<api.TextSearchResponseItem>
	undo?(current: api.TextSearchResponseItem, previously: api.TextSearchResponseItem): Promise<api.TextPatchResponse>
	className?: string
	actiontext: string
}

interface context {
	observation: api.Prediction
	prediction: api.Prediction
	statusText(p: api.Prediction): JSX.Element
}

export default function Approval(props: props): JSX.Element {
	const {
		item,
		threshold,
		color,
		statusText,
		approve,
		reject,
		undo,
		onChange,
		display: _display,
		reviewed: _reviewed,
		className,
		actiontext,
	} = props
	const [updated, setUpdated] = useState(item)
	const [loading, setLoading] = useState(false)
	const prediction = item.event?.approved || api.Prediction.NONE
	const observation = item.event?.observation || api.Prediction.NONE
	const confidence = item.event?.confidence || 0
	const agreement = prediction === observation
	const display_init = _display === api.Prediction.NONE ? prediction : _display
	const reviewed = _reviewed && observation === api.Prediction.INREVIEW
	const [feedback, setFeedback] = useState(true)
	const [status, setStatus] = useState<context>({
		observation: observation,
		prediction: display_init,
		statusText: statusText,
	})

	const updateStatus = (i: api.TextSearchResponseItem): api.TextSearchResponseItem => {
		const prediction = i.event?.approved || api.Prediction.NONE
		const observation = i.event?.observation || api.Prediction.NONE
		const undo_review = reviewed && !feedback
		const display_update = undo_review ? display_init : observation === api.Prediction.NONE ? prediction : observation
		setStatus({
			observation: observation,
			prediction: display_update,
			statusText: feedback ? predictions.string_changed : statusText,
		})
		setUpdated(i)
		return i
	}

	return (
		<bgapprovals.styles.ActionPanel
			background={color(status.prediction)}
			className={`${className} status-panel ${status.prediction === api.Prediction.INREVIEW ? "inreview" : "noreview"}`}
		>
			<bgapprovals.Status
				className="approval-status"
				color={color}
				confidence={confidence}
				threshold={threshold}
				p="0px"
				display={className === "extension" ? "none" : "inherit"}
				{...status}
			/>

			{!predictions.processing(display_init) && (
				<layouts.loading.screen loading={loading} cursor="not-allowed" opacity="0" icon={<></>} justifyContent="center">
					<layouts.overlays.Container>
						<layouts.overlays.Inline enabled={feedback}>
							<bgapprovals.styles.FeedbackContainer className={className}>
								<layouts.containers.span mr="10px">{actiontext}</layouts.containers.span>
								<layouts.containers.flex>
									<bgapprovals.styles.FeedbackYes
										active={observation === api.Prediction.NONE ? false : agreement}
										onClick={(evt) => {
											evt.stopPropagation()
											setLoading(true)
											approve(item)
												.then(updateStatus)
												.then(onChange)
												.then(() => setFeedback(false))
												.finally(() => setLoading(false))
										}}
									/>
									<bgapprovals.styles.FeedbackNo
										active={observation === api.Prediction.NONE ? false : !agreement}
										onClick={(evt) => {
											evt.stopPropagation()
											setLoading(true)
											reject(item)
												.then(updateStatus)
												.then(onChange)
												.then(() => setFeedback(false))
												.finally(() => setLoading(false))
										}}
									/>
								</layouts.containers.flex>
							</bgapprovals.styles.FeedbackContainer>
						</layouts.overlays.Inline>
						<layouts.overlays.Inline enabled={!feedback && undo !== undefined}>
							<bgapprovals.styles.FeedbackContainer>
								<bgapprovals.styles.FeedbackUndo
									onClick={(evt) => {
										evt.stopPropagation()
										setLoading(true)
										undo!(updated, item)
											.then(updateStatus)
											.then(onChange)
											.then(() => setFeedback(true))
											.catch((cause) => console.error("unable to display brandguard text approval", cause))
											.finally(() => setLoading(false))
									}}
								/>
							</bgapprovals.styles.FeedbackContainer>
						</layouts.overlays.Inline>
					</layouts.overlays.Container>
				</layouts.loading.screen>
			)}
		</bgapprovals.styles.ActionPanel>
	)
}

Approval.defaultProps = {
	display: api.Prediction.NONE,
	threshold: 0,
	reviewed: false,
	color: predictions.color,
	statusText: predictions.string,
	actiontext: "Do you agree?",
}
